/* The caller's latest multi-cost-center Actuals export, shared by every page and tab.
 *
 * The server is the source of truth (see useActualsExportPoller). This slice keeps only what the UI
 * needs to block a second export and to report progress; it never holds the download URL.
 * Persistence is a listener effect (actualsExportPersistence), so the reducers stay pure.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { eLocalStorageKeys } from 'src/constants/generic-constants';
import type { ActualsExportTrackingState } from 'src/models/ActualsExportModels';
import type { RootState } from 'src/store/store';
import { EMPTY_ACTUALS_EXPORT_TRACKING_STATE, parseActualsExportTrackingState } from 'src/utils/actuals-export-utils';
import { load } from 'src/utils/local-storage';

/** Copies only the tracking fields, so nothing else (a URL above all) can reach the store or storage. */
const pickTrackingFields = ({
  exportId,
  status,
  ccCount,
  fileName,
  requestedAt,
  errorCode
}: ActualsExportTrackingState): ActualsExportTrackingState => ({
  exportId,
  status,
  ccCount,
  fileName,
  requestedAt,
  errorCode
});

/** The persisted export, or the empty state when nothing valid is stored. */
export const loadPersistedActualsExport = (): ActualsExportTrackingState =>
  parseActualsExportTrackingState(load(eLocalStorageKeys.ActualsExport)) ?? { ...EMPTY_ACTUALS_EXPORT_TRACKING_STATE };

const actualsExportSlice = createSlice({
  name: 'actualsExport',
  // Lazy initializer: rehydrates from localStorage when the store is created.
  initialState: loadPersistedActualsExport,
  reducers: {
    /** This tab learned about an export (created, polled or reconciled). Persisted. */
    actualsExportTracked: (_state, action: PayloadAction<ActualsExportTrackingState>) => pickTrackingFields(action.payload),
    /** The export no longer exists for this user. Persisted (the stored entry is removed). */
    actualsExportCleared: () => ({ ...EMPTY_ACTUALS_EXPORT_TRACKING_STATE }),
    /** Another tab already persisted this state; it is applied here without writing it back. */
    actualsExportSyncedFromOtherTab: (_state, action: PayloadAction<ActualsExportTrackingState>) => pickTrackingFields(action.payload)
  }
});

export const { actualsExportTracked, actualsExportCleared, actualsExportSyncedFromOtherTab } = actualsExportSlice.actions;

export const selectActualsExport = (state: RootState): ActualsExportTrackingState => state.actualsExportStore;

export default actualsExportSlice.reducer;
